'use client'

import { useQueries } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { accountExchangeKeys, fetchAccountExchanges } from '@/service/account-exchanges.hook'
import { useUserAccounts } from '@/service/account.hook'
import { isExchangeEnabled, useVenues } from '@/service/venue.hook'
import { useVenueHealthStreams } from '@/service/ws/use-venue-health-stream'
import { Exchange, TradeVenue } from '@/types/generated/common/types'
import { ExchangeStatusType, type VenueHealth, type VenueInfo } from '@/types/generated/public_api'

export type VenueTradingIssueKind = 'disabled' | 'trading-down' | 'outage' | 'maintenance'

export interface VenueTradingIssue {
  exchange: Exchange
  kind: VenueTradingIssueKind
  /** Epoch ms the condition began, when the health monitor reports it. */
  sinceMs: number | null
  /**
   * The order is certain to be rejected (routing is switched off server-side), so submit is
   * blocked. The rest are warnings: a probe can lag a venue's recovery, so the user may still try.
   */
  blocksOrders: boolean
}

/**
 * Why orders on `exchange` would fail right now, or null. Two sources:
 * - GetVenues `enabled: false` — order routing is disabled at the public-api boundary.
 * - The venue-health monitor — `trading` is a live order round-trip probe; `data` is the
 *   venue's own beacon (MAINTENANCE vs UNPLANNED_OUTAGE). A data outage only warns while no
 *   probe says trading is fine (trading recovers independently of data).
 * `venues` undefined (still loading) and no health yet both mean "no known issue".
 */
export function venueTradingIssue(
  exchange: Exchange,
  venues: VenueInfo[] | undefined,
  health: VenueHealth | undefined
): VenueTradingIssue | null {
  if (venues !== undefined && !isExchangeEnabled(venues, exchange)) {
    return { exchange, kind: 'disabled', sinceMs: null, blocksOrders: true }
  }
  if (!health) return null
  const since = Number(health.sinceMs)
  const sinceMs = Number.isFinite(since) && since > 0 ? since : null
  const issue = (kind: VenueTradingIssueKind): VenueTradingIssue => ({
    exchange,
    kind,
    sinceMs,
    blocksOrders: false,
  })
  if (health.trading === false) return issue('trading-down')
  if (health.trading === true) return null
  if (health.data === ExchangeStatusType.EXCHANGE_STATUS_TYPE_MAINTENANCE)
    return issue('maintenance')
  if (health.data === ExchangeStatusType.EXCHANGE_STATUS_TYPE_UNPLANNED_OUTAGE)
    return issue('outage')
  return null
}

/**
 * Live trading issues for the given venues (deduped). Subscribes to the venue-health stream
 * for just those venues while mounted.
 */
export function useVenueTradingIssues(exchanges: readonly Exchange[]): VenueTradingIssue[] {
  const exchangesKey = useMemo(
    () =>
      [
        ...new Set(
          exchanges.filter(
            (exchange) =>
              exchange !== Exchange.EXCHANGE_UNSPECIFIED && exchange !== Exchange.UNRECOGNIZED
          )
        ),
      ]
        .sort((a, b) => a - b)
        .join(','),
    [exchanges]
  )
  const uniqueExchanges = useMemo(
    () => (exchangesKey ? exchangesKey.split(',').map(Number) : []) as Exchange[],
    [exchangesKey]
  )
  const payloads = useMemo(
    () => uniqueExchanges.map((exchange) => ({ exchange })),
    [uniqueExchanges]
  )
  const { data: venueList } = useVenues()
  const [healthByExchange, setHealthByExchange] = useState<ReadonlyMap<Exchange, VenueHealth>>(
    new Map()
  )

  useVenueHealthStreams({
    connected: payloads.length > 0,
    payloads,
    onMessage: (health) => {
      setHealthByExchange((current) => {
        const previous = current.get(health.exchange)
        if (
          previous &&
          previous.data === health.data &&
          previous.trading === health.trading &&
          previous.sinceMs === health.sinceMs
        ) {
          return current
        }
        const next = new Map(current)
        next.set(health.exchange, health)
        return next
      })
    },
  })

  return useMemo(
    () =>
      uniqueExchanges.flatMap((exchange) => {
        const issue = venueTradingIssue(exchange, venueList?.venues, healthByExchange.get(exchange))
        return issue ? [issue] : []
      }),
    [healthByExchange, uniqueExchanges, venueList]
  )
}

/**
 * Trading issues across every venue the user has an account on — what the connection-status
 * surfaces (side panel, mobile nav, the per-venue hover card) show. Each venue resolves to its
 * primary exchange exactly as useAccountPrimaryExchange does, through the same cached
 * getExchanges queries, so the summary and the per-venue rows can't disagree.
 */
export function useConnectedVenueIssues(): VenueTradingIssue[] {
  const { data: accountsData } = useUserAccounts()
  const tradeVenues = useMemo(
    () =>
      [...new Set((accountsData?.accounts ?? []).map((account) => account.tradeVenue))]
        .filter((venue) => venue !== TradeVenue.TRADE_VENUE_UNSPECIFIED)
        .sort((a, b) => a - b),
    [accountsData?.accounts]
  )
  const exchangeQueries = useQueries({
    queries: tradeVenues.map((venue) => ({
      queryKey: accountExchangeKeys.byVenue(venue),
      queryFn: () => fetchAccountExchanges(venue),
      staleTime: 60 * 60 * 1000,
    })),
  })
  const exchanges = tradeVenues.map((venue, index) => {
    const query = exchangeQueries[index]
    if (query?.data && query.data.length > 0) return query.data[0]
    if (!query || query.isPending) return Exchange.EXCHANGE_UNSPECIFIED
    return toExchangeEnum(tradeVenueToExchangeKey(venue))
  })
  return useVenueTradingIssues(exchanges)
}
